import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
} from '@mui/material';
import {
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Payment as PaymentIcon,
  Block as BlockIcon,
  Copyright as CopyrightIcon,
  Update as UpdateIcon,
  Policy as PolicyIcon,
} from '@mui/icons-material';

const TermsOfService: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom>
          Terms of Service
        </Typography>
        
        <Typography variant="body1" paragraph>
          Last updated: {new Date().toLocaleDateString()}
        </Typography>

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <GavelIcon sx={{ mr: 1 }} />
            Agreement to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By accessing or using Amsler Grid Daily, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the service.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <AssignmentIcon sx={{ mr: 1 }} />
            Service Description
          </Typography>
          <Typography variant="body1" paragraph>
            Amsler Grid Daily is a medical self-assessment tool designed to help users monitor their eye health. The service is available across multiple platforms:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Web Application"
                secondary="Access through any modern web browser at amslergriddaily.web.app"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Android Application"
                secondary="Available on Google Play Store for Android devices running Android 8.0 or later"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="iOS Application"
                secondary="Available on App Store for iPhone and iPad running iOS 14.0 or later"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="macOS Application"
                secondary="Available on Mac App Store for Mac computers running macOS 11.0 or later"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PolicyIcon sx={{ mr: 1 }} />
            Platform-Specific Terms
          </Typography>
          
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Android Platform
          </Typography>
          <Typography variant="body1" paragraph>
            By using the Android application, you agree to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Google Play Store Terms"
                secondary="Comply with Google Play Store's terms of service and policies"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Device Requirements"
                secondary="Use a compatible Android device with required sensors and capabilities"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Permissions"
                secondary="Grant necessary permissions for camera access and data storage"
              />
            </ListItem>
          </List>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            iOS, iPadOS, and macOS Platforms
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" component="div">
              <strong>Apple End User License Agreement</strong>
              <br />
              This application is licensed, not sold, to you. Your license to this App is subject to your prior acceptance of Apple's Licensed Application End User License Agreement ("Standard EULA").
              <br />
              <br />
              By using the iOS, iPadOS, or macOS application, you agree to be bound by Apple's Standard EULA. The Standard EULA governs your use of this Licensed Application and any content, materials, or services accessible from or purchased within the Licensed Application.
              <br />
              <br />
              <a 
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                View Apple's Standard EULA →
              </a>
            </Typography>
          </Alert>
          <Typography variant="body1" paragraph>
            By using the iOS, iPadOS, or macOS application, you also agree to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="App Store Terms"
                secondary="Comply with Apple's App Store terms of service and policies"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Device Requirements"
                secondary="Use a compatible Apple device with required sensors and capabilities"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="License Scope"
                secondary="The App is licensed, not sold, and may only be used on Apple-branded products you own or control"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="No Warranty"
                secondary="The App is provided 'AS IS' without warranty of any kind"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Liability Limitation"
                secondary="Licensor's total liability is limited to $50.00 (as per Apple's Standard EULA)"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <SecurityIcon sx={{ mr: 1 }} />
            User Responsibilities
          </Typography>
          <Typography variant="body1" paragraph>
            As a user of Amsler Grid Daily, you agree to:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Accurate Information"
                secondary="Provide accurate and complete information when using the service"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Account Security"
                secondary="Maintain the security of your account credentials"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Appropriate Use"
                secondary="Use the service only for its intended purpose of eye health monitoring"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PaymentIcon sx={{ mr: 1 }} />
            Subscription Terms
          </Typography>
          <Typography variant="body1" paragraph>
            Premium features of Amsler Grid Daily are available through subscription:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Billing"
                secondary="Subscriptions are billed according to your chosen plan (monthly/annually)"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Cancellation"
                secondary="You may cancel your subscription at any time through your account settings"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Refunds"
                secondary="Refund policies vary by platform (iOS, Android, Web) and are subject to respective store policies"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BlockIcon sx={{ mr: 1 }} />
            Limitations and Disclaimers
          </Typography>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Amsler Grid Daily is not a substitute for professional medical care. Always consult healthcare professionals for medical advice.
          </Alert>
          <Typography variant="body1" paragraph>
            The service is provided "as is" without warranties of any kind, either express or implied. We do not guarantee that:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Service Availability"
                secondary="The service will be uninterrupted or error-free"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="Medical Accuracy"
                secondary="The service will detect all eye health conditions or changes"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <CopyrightIcon sx={{ mr: 1 }} />
            Intellectual Property
          </Typography>
          <Typography variant="body1" paragraph>
            All content and functionality in Amsler Grid Daily are protected by copyright and other intellectual property rights:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Content Ownership"
                secondary="All content remains the property of Amsler Grid Daily or its licensors"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="User Data"
                secondary="You retain rights to your personal data while granting us license to use it for service provision"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <UpdateIcon sx={{ mr: 1 }} />
            Changes to Terms
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to modify these terms at any time. We will notify users of any material changes through:
          </Typography>
          <List>
            <ListItem>
              <ListItemText 
                primary="Email Notifications"
                secondary="Important changes will be communicated via email"
              />
            </ListItem>
            <ListItem>
              <ListItemText 
                primary="In-App Notices"
                secondary="Updates will be displayed within the application"
              />
            </ListItem>
          </List>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ my: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PolicyIcon sx={{ mr: 1 }} />
            Governing Law
          </Typography>
          <Typography variant="body1" paragraph>
            These terms are governed by and construed in accordance with applicable laws, without regard to its conflict of law principles.
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mt: 4 }}>
          For questions about these Terms of Service, please contact us through our support channels.
        </Alert>
      </Paper>
    </Container>
  );
};

export default TermsOfService; 